import type {
  DiscoverApi,
  DiscoverCompaniesParams,
  DiscoverCompany,
  DiscoverOutreachDraft,
  DiscoverOutreachInput,
  DiscoverPerson,
  DiscoverPreferredCompany,
} from "@/features/discover/discoverContracts";
import {
  normalizeCompanyName,
  SYNTHETIC_DISCOVER_PERSON_ID_PREFIX,
} from "@/features/discover/discoverContracts";
import { httpClient } from "@/shared/api/httpClient";

// The foundation-branch payload shapes aren't finalized, so every response is
// coerced defensively: we accept either a bare array or a single-key envelope
// ({ companies }, { people }, { preferredCompanies }) and tolerate a few field
// name variants. Anything missing degrades to a safe default rather than
// throwing mid-render. 503 (DISCOVER_ENABLED=false) is intentionally NOT caught
// here - it propagates so the hook can render the "disabled" state.

function asRecords(raw: unknown, ...keys: string[]): Record<string, unknown>[] {
  let list: unknown = raw;
  if (!Array.isArray(list) && raw && typeof raw === "object") {
    for (const key of keys) {
      const candidate = (raw as Record<string, unknown>)[key];
      if (Array.isArray(candidate)) {
        list = candidate;
        break;
      }
    }
  }
  if (!Array.isArray(list)) return [];
  return list.filter(
    (item): item is Record<string, unknown> => Boolean(item) && typeof item === "object",
  );
}

function asRecord(raw: unknown, ...keys: string[]): Record<string, unknown> {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const key of keys) {
      const candidate = (raw as Record<string, unknown>)[key];
      if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
        return candidate as Record<string, unknown>;
      }
    }
    return raw as Record<string, unknown>;
  }
  if (Array.isArray(raw) && raw[0] && typeof raw[0] === "object") {
    return raw[0] as Record<string, unknown>;
  }
  return {};
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function normalizeCompany(item: Record<string, unknown>): DiscoverCompany | null {
  const name = str(item.name) ?? str(item.company) ?? str(item.companyName);
  if (!name) return null;
  const key =
    str(item.key) ?? str(item.normalizedName) ?? str(item.id) ?? normalizeCompanyName(name);
  return {
    key,
    name,
    contactCount: num(item.contactCount ?? item.count ?? item.peopleCount),
    preferred: item.preferred === true || item.isPreferred === true,
  };
}

function normalizePerson(item: Record<string, unknown>, index: number): DiscoverPerson {
  const name = str(item.name) ?? str(item.fullName) ?? "Unknown contact";
  const sourcesRaw = item.sources;
  const sources = Array.isArray(sourcesRaw)
    ? sourcesRaw
        .map((entry) => {
          if (typeof entry === "string") return entry;
          if (entry && typeof entry === "object" && "kind" in entry) {
            return str((entry as { kind: unknown }).kind);
          }
          return null;
        })
        .filter((value): value is string => Boolean(value))
    : [];
  return {
    id: str(item.id) ?? str(item.contactId) ?? `${SYNTHETIC_DISCOVER_PERSON_ID_PREFIX}${index}`,
    name,
    title: str(item.title) ?? str(item.jobTitle) ?? str(item.role),
    company: str(item.company) ?? str(item.companyName),
    relationshipStrength: num(item.relationshipStrength),
    linkedInUrl: str(item.linkedInUrl) ?? str(item.linkedinUrl) ?? str(item.linkedIn),
    email: str(item.email) ?? str(item.primaryEmail),
    lastActivityAt: str(item.lastActivityAt) ?? str(item.lastContactedAt),
    sources,
  };
}

function normalizePreferred(item: Record<string, unknown>): DiscoverPreferredCompany | null {
  const id = str(item.id) ?? str(item.preferredId);
  const name = str(item.name) ?? str(item.company) ?? str(item.companyName);
  if (!id || !name) return null;
  return {
    id,
    name,
    normalizedName: str(item.normalizedName) ?? str(item.key) ?? normalizeCompanyName(name),
  };
}

function normalizeOutreachDraft(raw: unknown): DiscoverOutreachDraft {
  const root = asRecord(raw, "draft", "outreachDraft", "data");
  const email = asRecord(root.email, "email");
  return {
    email: {
      subject: str(email.subject) ?? str(email.subjectLine) ?? "",
      body: str(email.body) ?? str(email.text) ?? "",
    },
    linkedInDm: str(root.linkedInDm) ?? str(root.linkedinDm) ?? str(root.linkedInMessage) ?? "",
    contextSummary: str(root.contextSummary) ?? str(root.context) ?? str(root.summary) ?? "",
  };
}

export const realDiscoverApi: DiscoverApi = {
  async listCompanies({ view, search, sort }: DiscoverCompaniesParams): Promise<DiscoverCompany[]> {
    const params = new URLSearchParams({ view });
    // Only send a search term when the user has typed something - an empty
    // `search=` would be a no-op but adds noise to the request.
    const trimmed = search?.trim();
    if (trimmed) params.set("search", trimmed);
    if (sort) params.set("sort", sort);
    const raw = await httpClient.get<unknown>(`/api/discover/companies?${params.toString()}`);
    return asRecords(raw, "companies", "items", "data")
      .map(normalizeCompany)
      .filter((company): company is DiscoverCompany => company !== null);
  },

  async listPeople(companyKey: string): Promise<DiscoverPerson[]> {
    const raw = await httpClient.get<unknown>(
      `/api/discover/companies/${encodeURIComponent(companyKey)}/people`,
    );
    return asRecords(raw, "people", "contacts", "items", "data").map(normalizePerson);
  },

  async listPreferredCompanies(): Promise<DiscoverPreferredCompany[]> {
    const raw = await httpClient.get<unknown>("/api/discover/preferred-companies");
    return asRecords(raw, "preferredCompanies", "companies", "items", "data")
      .map(normalizePreferred)
      .filter((row): row is DiscoverPreferredCompany => row !== null);
  },

  async addPreferredCompany(name: string): Promise<DiscoverPreferredCompany> {
    const raw = await httpClient.post<unknown>("/api/discover/preferred-companies", { name });
    const row = normalizePreferred(asRecord(raw, "preferredCompany", "company", "data"));
    // The POST should echo the created row; if the backend returns something we
    // can't read, fall back to a synthetic row so the caller still resolves.
    return (
      row ?? {
        id: normalizeCompanyName(name),
        name,
        normalizedName: normalizeCompanyName(name),
      }
    );
  },

  async removePreferredCompany(id: string): Promise<void> {
    await httpClient.del(`/api/discover/preferred-companies/${encodeURIComponent(id)}`);
  },

  // Generate only - the backend never sends, drafts, or persists. 503
  // (DISCOVER_OUTREACH_ENABLED=false) is intentionally NOT caught here; it
  // propagates so the modal can show the "not enabled yet" state.
  async createOutreachDraft(
    companyKey: string,
    input: DiscoverOutreachInput,
  ): Promise<DiscoverOutreachDraft> {
    const raw = await httpClient.post<unknown>(
      `/api/discover/companies/${encodeURIComponent(companyKey)}/outreach-draft`,
      input as unknown as Record<string, unknown>,
    );
    return normalizeOutreachDraft(raw);
  },
};
